/**
 * The closeup inspection tour's own review pages: every capture of a tour, in
 * order, with a full-screen viewer to look closely and a flag and a note per
 * picture (/proves/inspeccio/:code), and the list of every tour a campaign
 * ran (/proves/inspeccions).
 */

/** The Arma 3 DLCs by the platform's own keys (ArmaDlc), as the Launcher names them. */
const DLC_NAMES: Record<string, string> = {
  apex: 'Apex',
  contact: 'Contact',
  gm: 'Global Mobilization',
  vn: 'S.O.G. Prairie Fire',
  csla: 'CSLA Iron Curtain',
  ws: 'Western Sahara',
  spe: 'Spearhead 1944',
  rf: 'Reaction Forces',
  ef: 'Expeditionary Forces',
};

export const ca_inspection = {
  inspectionsPage: {
    title: 'Inspeccions',
    intro:
      'Cada gira de proximitat que una campanya d’inspecció ha fet, amb les captures que hi ha i quantes ja estan revisades.',
    none: 'Encara no s’ha fet cap gira.',
    recipeWorld: (recipe: string, world: string) => `${recipe} · ${world}`,
    captures: (n: number) => `${n} captures`,
    reviewed: (done: number, total: number) => `${done} de ${total} revisades`,
    open: 'Obre la revisió',
    openCampaign: 'Veure la campanya',
    noCaptures: 'Sense captures encara.',
    withheld: (n: number) => `${n} retingudes`,
  },
  inspectionReviewPage: {
    title: (recipe: string) => `Inspecció · ${recipe}`,
    back: 'Totes les inspeccions',
    missionFacts: 'La missió',
    summary: 'Resum',
    objectives: 'Objectius',
    noObjectives: 'Cap objectiu.',
    insertion: 'Com s’entra',
    extraction: 'Com se surt',
    base: 'La base',
    timeWeather: 'Hora i temps',
    /** C1: TestModeFiles.daylight publishes every inspection tour at noon so the photos show something. */
    photographedAtNoon: (missionHour: string) =>
      `Fotografiat a les 12:00 per a la inspecció; la missió és a les ${missionHour}.`,
    twists: 'Girs',
    noTwists: 'Cap gir en aquesta missió.',
    decidedBy: (who: string) => `(ho decideix ${who})`,
    decidedByNames: {
      generator: 'la generació',
      director: 'qui dirigeix',
      vote: 'una votació',
      commander: 'el comandament',
    } as Record<string, string>,
    means: {
      foot: 'a peu',
      vehicle: 'amb vehicle',
      helicopter: 'amb helicòpter',
      boat: 'amb barca',
      deployed: 'ja desplegats',
    } as Record<string, string>,
    baseKinds: {
      fob: 'base avançada',
      camp: 'campament',
      airfield: 'aeròdrom',
      ship: 'vaixell',
      patrol_base: 'base de patrulla',
    } as Record<string, string>,
    captures: 'Captures',
    noCaptures: 'Aquesta sessió no té cap captura de la gira.',
    /** The tour as a whole: where it stands, its verdict, the comment on it. */
    tour: {
      title: 'La gira sencera',
      states: {
        pending: 'Pendent de revisar',
        in_progress: 'Revisió en curs',
        reviewed: 'Revisada',
        superseded: 'Obsoleta',
        old: 'Antiga',
        no_captures: 'Sense captures',
        withheld: 'Captures retingudes',
        dismissed: 'Descartada',
      } as Record<string, string>,
      reasons: {
        superseded: (by: number | null) =>
          by
            ? `La campanya ${by} va tornar a fer aquesta missió en aquest mapa amb una versió més nova.`
            : 'Una gira més nova va tornar a fer aquesta missió en aquest mapa.',
        old: 'Fa més de catorze dies que es va fer i ningú n’ha marcat cap captura.',
        no_captures: 'La gira no va fer cap captura: no hi ha res a mirar.',
        withheld:
          'Totes les captures són d’abans que el watcher només pogués fotografiar el joc. Obre-les i deixa veure o esborra cadascuna.',
        dismissed: 'Algú la va descartar a mà.',
        reopened: 'Es revisa tot i estar substituïda, perquè algú ho ha demanat.',
      },
      reviewAnyway: 'Revisa-la igualment',
      dismiss: 'No cal revisar-la',
      dismissReason: 'Per què no cal? (una frase)',
      dismissConfirm: 'Descarta la gira',
      undoDismiss: 'Torna-la a la llista per revisar',
      comment: 'Comentari general de la gira',
      commentPlaceholder: 'La base, la composició, el que no toca a cap foto en concret…',
      verdict: 'Veredicte de la gira',
      verdictHint:
        'En triar-ne un la gira passa a «Revisades», encara que quedin captures sense marcar.',
      markReviewed: 'Marca-la com a revisada',
      markedReviewed: 'Revisada',
      markReviewedHint:
        'Per quan ja l’has mirada tot i no haver marcat totes les captures. Torna-hi a clicar per desfer-ho.',
      verdicts: {
        reviewed: 'Revisada',
        correct: 'Correcta',
        problems: 'Amb problemes',
        inconclusive: 'No concloent',
      } as Record<string, string>,
      verdictNote: 'Una frase: què cal canviar, o què ha quedat bé',
      clearVerdict: 'Treu el veredicte',
      decided: (who: string) => `Valorada per ${who}`,
      version: (v: string) =>
        v === 'dev' ? 'versió de desenvolupament' : `v${v.replace(/^v/, '')}`,
    },
    filters: {
      label: 'Mostra',
      all: 'Totes',
      pending: 'Pendents',
      problem: 'Amb problema',
      flagged: 'Avisades',
    } as Record<string, string>,
    filterCount: (shown: number, total: number) => `${shown} de ${total}`,
    noneInFilter: 'Cap captura en aquest filtre.',
    agentNote: 'Nota de Claude',
    agentVerdicts: {
      correct: 'correcte',
      problem: 'problema',
      useless: 'inútil',
    } as Record<string, string>,
    open: 'Amplia',
    cardNotePlaceholder: 'Què hi veus? Es desa sol.',
    advance: 'Passa a la següent en marcar',
    viewerProgress: (n: number, total: number, pending: number) =>
      pending === 0
        ? `${n} de ${total} · totes marcades`
        : `${n} de ${total} · ${pending} pendents`,
    nextPending: 'Següent pendent (N)',
    reviewAll: 'Revisa el que queda',
    hotkey: 'Captura feta a mà',
    /** Shown next to "Totes les inspeccions" when the tour belongs to a campaign, back to its one-page review (build item: campaign review). */
    campaignReview: 'Revisió de tota la campanya',
    autoFindings: 'Troballes automàtiques',
    progress: (done: number, total: number) => `Revisades ${done} de ${total}`,
    markGroupCorrect: 'Tot el grup correcte',
    expectLabel: 'Hauries de veure:',
    whyLabel: 'Per què:',
    lookLabel: 'Fixa’t en:',
    contextCard: 'Abans de mirar les fotos',
    flags: {
      correct: 'Sembla correcte',
      problem: 'Problema',
      useless: 'Inútil',
      repeat: 'Repetida',
      mismatch: 'No correspon',
    } as Record<string, string>,
    noteLabel: 'Nota',
    notePlaceholder: 'Alguna cosa a dir sobre aquesta captura?',
    saved: 'Desat',
    saving: 'Desant…',
    reviewedBy: (who: string) => `Revisat per ${who}`,
    unreviewed: 'Sense revisar',
    viewerClose: 'Tanca (Esc)',
    viewerPrev: 'Anterior (←)',
    viewerNext: 'Següent (→)',
    viewerCount: (n: number, total: number) => `${n} de ${total}`,
    /** A picture kept before the capture guard, with nothing to show it is the game's own window. */
    withheld: 'Retinguda',
    withheldHint:
      'Aquesta captura és d’abans que el watcher només pogués capturar la finestra del joc: pot mostrar l’escriptori o una altra aplicació. Només un administrador la veu fins que la deixa veure o l’esborra.',
    withheldPlayer: 'Imatge retinguda fins que un administrador la revisi.',
    release: 'És del joc: deixa-la veure',
    remove: 'Esborra-la',
    removeConfirm: 'Esborrar aquesta captura per sempre?',
    moderationFailed: 'No s’ha pogut desar:',
  },
  inspectionSummaryPage: {
    title: 'Resum de les inspeccions',
    intro:
      'Cada revisió i cada senyal automàtic que les campanyes d’inspecció han deixat, agrupats per recepta, tipus d’objectiu, tipus de parada i força — un sol full per a la correcció general.',
    overall: (reviewed: number, total: number) =>
      `${reviewed} de ${total} captures revisades en total.`,
    byRecipe: 'Per recepta',
    byObjectiveType: 'Per tipus d’objectiu',
    byStopKind: 'Per tipus de parada',
    byForceRole: 'Per força',
    noGroups: 'Encara no hi ha res per mostrar-hi.',
    group: 'Grup',
    total: 'Captures',
    reviewed: 'Revisades',
    flags: {
      correct: 'Correcte',
      problem: 'Problema',
      useless: 'Inútil',
      repeat: 'Repetida',
      mismatch: 'No correspon',
    },
    qc: {
      empty: 'Buida',
      dark: 'Fosca',
      duplicate: 'Repetida (QC)',
    },
    notes: 'Notes',
    noNotes: 'Encara no hi ha cap nota.',
    openSession: 'Obre la revisió',
  },
  inspectionCampaignStatusPage: {
    title: (id: string) => `Campanya d’inspecció ${id}`,
    back: 'Totes les inspeccions',
    progress: (current: number, total: number) =>
      `Element ${Math.min(current + 1, total)} de ${total}`,
    stop: 'Atura la campanya',
    resume: 'Reprèn',
    openReview: 'Obre la revisió',
    /** Link to the campaign's own one-page review, next to the back link. */
    openCampaignReview: 'Revisió de tota la campanya en una pàgina',
    captures: (n: number) => `${n} captures`,
    retried: (n: number) => `Reintentat ${n} ${n === 1 ? 'vegada' : 'vegades'}`,
    /**
     * An item's error as a person reads it. The platform writes codes; the two
     * DLC refusals (2026-09-27: a lent PC with no DLC sent to Tanoa) carry the
     * DLCs after a colon, and get a sentence. Anything else is shown as written.
     */
    itemError: (error: string) => {
      const [code, rest] = error.split(':', 2);
      const names = (rest ?? '')
        .split(',')
        .map((k) => DLC_NAMES[k.trim()] ?? k.trim())
        .filter((n) => n.length > 0)
        .join(', ');
      if (code === 'dlc_missing') {
        return `No s’ha llançat: la màquina deixada no té ${names}, que aquest element necessita.`;
      }
      if (code === 'dlc_unknown') {
        return `No s’ha llançat: aquest element necessita ${names} i el watcher de la màquina deixada encara no ha dit quins DLC té (cal una versió nova del watcher, oberta).`;
      }
      if (code === 'machine_locked') {
        return 'No s’ha llançat: la màquina deixada està bloquejada (la pantalla d’inici de sessió de Windows) i cal que algú la desbloquegi.';
      }
      return error;
    },
    states: {
      running: 'En marxa',
      done: 'Acabada',
      failed: 'Fallida',
      cancelled: 'Aturada',
    } as Record<string, string>,
    itemStates: {
      pending: 'Pendent',
      starting: 'Generant',
      waiting_live: 'Esperant que arrenqui',
      connecting: 'Connectant el watcher',
      settling: 'Assentant-se',
      inspecting: 'Fent la gira',
      disconnecting: 'Desconnectant',
      done: 'Feta',
      failed: 'Fallida',
    } as Record<string, string>,
  },
  inspectionCampaignReviewPage: {
    title: (id: string) => `Revisió de la campanya ${id}`,
    back: 'Torna a la campanya',
    chapterIndex: 'Gires d’aquesta campanya',
    /** N revisades, M en total, K amb el flag "problem" — one counter for the whole campaign. */
    progress: (reviewed: number, total: number, problems: number) =>
      `${reviewed} de ${total} revisades · ${problems} amb problema`,
    openTourAlone: 'Obre-la sola',
    noTours: 'Aquesta campanya encara no ha acabat cap gira amb captures.',
    loading: 'Carregant les gires…',
    chapterTitle: (recipe: string, world: string) => `${recipe} · ${world}`,
    markAllReviewed: 'Marca la campanya com a revisada',
    markAllTitle: 'Marcar tota la campanya com a revisada?',
    markAllBody: (n: number) =>
      n === 1
        ? 'La gira que queda per revisar passarà a «Revisades», encara que tingui captures sense marcar. Es pot desfer gira per gira.'
        : `Les ${n} gires que queden per revisar passaran a «Revisades», encara que tinguin captures sense marcar. Es pot desfer gira per gira.`,
    markAllDone: 'Tota la campanya està revisada.',
  },
};
